import { useEffect, useId, useRef, useState } from 'react';

/** A horizontal feed: label + sideways-scrolling track with prev/next arrows. */
export function Rail({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  const headingId = useId();
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const update = () => {
      setAtStart(el.scrollLeft <= 1);
      setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const scroll = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: reduce ? 'auto' : 'smooth' });
  };

  return (
    <section className="mt-12">
      <h2 id={headingId} className="mb-4 font-heading text-xl font-bold tracking-tight">{label}</h2>
      <div className="group relative">
        <div
          ref={trackRef}
          tabIndex={0}
          role="group"
          aria-labelledby={headingId}
          className="no-scrollbar -mx-6 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-px-6 rounded-xl px-6 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-provny-cyber/60"
        >
          {children}
        </div>

        {/* edge fades — dissolve content into the page bg at each scrollable end */}
        {!atStart && (
          <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-provny-white to-transparent dark:from-provny-ink" />
        )}
        {!atEnd && (
          <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-provny-white to-transparent dark:from-provny-ink" />
        )}

        {/* prev / next arrows */}
        <RailArrow dir="left" onClick={() => scroll(-1)} hidden={atStart} />
        <RailArrow dir="right" onClick={() => scroll(1)} hidden={atEnd} />
      </div>
    </section>
  );
}

/** Brand-styled scroll button — dark disc, stroke chevron, cyan on hover. */
function RailArrow({
  dir,
  onClick,
  hidden,
}: {
  dir: 'left' | 'right';
  onClick: () => void;
  hidden: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir === 'left' ? 'Scroll left' : 'Scroll right'}
      tabIndex={hidden ? -1 : 0}
      className={`absolute top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-provny-border bg-provny-white/80 text-provny-black backdrop-blur transition hover:border-provny-cyber hover:text-provny-cyber focus-visible:outline-none focus-visible:border-provny-cyber dark:border-white/20 dark:bg-provny-ink/70 dark:text-white ${
        dir === 'left' ? 'left-2' : 'right-2'
      } ${hidden ? 'pointer-events-none opacity-0' : 'opacity-70 group-hover:opacity-100'}`}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        {dir === 'left' ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
      </svg>
    </button>
  );
}

/** One fixed-width, snap-aligned item in a Rail. Cyan outline on hover. */
export function Slide({ width, children }: { width: string; children: React.ReactNode }) {
  return (
    <div
      className={`${width} shrink-0 snap-start rounded-xl ring-1 ring-transparent transition duration-200 hover:ring-provny-cyber/70`}
    >
      {children}
    </div>
  );
}
